/**
 * SummaryPanel - Live list/cart/comparison view component
 *
 * Displays collected items from the chat panel in a sidebar.
 * Updates in real-time as items are added/removed.
 *
 * Features:
 * - Item list with remove buttons
 * - Running total display
 * - Clear all action
 * - Action buttons (Compare, Book, Checkout)
 * - Mobile slide-over behavior
 */

import { getListStore, type ListItem, type SharedListStore, type ContextAction } from '../core/list-store';

/** Max renders per second before circuit breaker triggers */
const MAX_RENDERS_PER_SECOND = 10;

export interface SummaryPanelConfig {
  title?: string;
  emptyMessage?: string;
  showTotal?: boolean;
  actions?: SummaryAction[];
  onAction?: (action: string, items: ListItem[]) => void;
  onClose?: () => void;
}

export interface SummaryAction {
  label: string;
  value: string;
  variant?: 'primary' | 'secondary';
  minItems?: number;
}

export class SummaryPanel {
  private container: HTMLElement;
  private config: SummaryPanelConfig;
  private listStore: SharedListStore;
  private unsubscribe: (() => void) | null = null;
  private isOpen: boolean = false;

  // Render safety: prevent infinite loops
  private renderCount: number = 0;
  private renderResetTimeout: ReturnType<typeof setTimeout> | null = null;
  private isCircuitBroken: boolean = false;

  constructor(container: HTMLElement, config: SummaryPanelConfig = {}) {
    this.container = container;
    this.config = {
      title: 'Your List',
      emptyMessage: 'No items added yet',
      showTotal: true,
      actions: [],
      ...config,
    };
    this.listStore = getListStore();

    this.render();
    this.subscribe();
  }

  /**
   * Subscribe to list store changes.
   */
  private subscribe(): void {
    this.unsubscribe = this.listStore.on('change', () => {
      this.render();
    });
  }

  /**
   * Render the summary panel.
   * Protected by circuit breaker to prevent infinite loops.
   */
  private render(): void {
    // Circuit breaker: prevent infinite render loops
    if (this.isCircuitBroken) {
      console.warn('[SummaryPanel] Circuit breaker active - render blocked');
      return;
    }

    this.renderCount++;
    if (this.renderCount > MAX_RENDERS_PER_SECOND) {
      console.error('[SummaryPanel] Too many renders per second - circuit breaker triggered');
      this.isCircuitBroken = true;
      this.container.innerHTML = `
        <div class="pms-summary-error">
          <p>Display error occurred. Please refresh the page.</p>
        </div>
      `;
      // Auto-reset after 5 seconds
      setTimeout(() => {
        this.isCircuitBroken = false;
        this.renderCount = 0;
      }, 5000);
      return;
    }

    // Reset counter after 1 second of no renders
    if (this.renderResetTimeout) {
      clearTimeout(this.renderResetTimeout);
    }
    this.renderResetTimeout = setTimeout(() => {
      this.renderCount = 0;
    }, 1000);

    const items = this.listStore.getItems();
    const total = this.listStore.getFormattedTotal();
    const count = items.length;
    const context = this.listStore.detectContext();

    this.container.innerHTML = '';

    // Preserve pms-split-summary class if present (set by parent container)
    // and add our panel-specific classes
    const baseClasses = this.container.classList.contains('pms-split-summary')
      ? 'pms-split-summary pms-summary-panel'
      : 'pms-summary-panel';

    // Remove old context classes before adding new one
    this.container.classList.forEach(cls => {
      if (cls.startsWith('pms-context-')) {
        this.container.classList.remove(cls);
      }
    });

    this.container.className = `${baseClasses} ${this.isOpen ? 'pms-open' : ''} pms-context-${context.type}`;

    // Header
    const header = document.createElement('div');
    header.className = 'pms-summary-header';
    header.innerHTML = `
      <div class="pms-summary-header-left">
        <h3 class="pms-summary-title">${this.escapeHtml(this.config.title || 'Your List')}</h3>
        ${count > 0 ? `<span class="pms-summary-count">${count}</span>` : ''}
      </div>
      <div class="pms-summary-header-actions">
        ${count > 0 ? `<button class="pms-summary-clear" title="Clear all">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>` : ''}
        <button class="pms-summary-close" title="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `;
    this.container.appendChild(header);

    // Bind header actions
    const clearBtn = header.querySelector('.pms-summary-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.handleClear());
    }

    const closeBtn = header.querySelector('.pms-summary-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Items list
    const itemsContainer = document.createElement('div');
    itemsContainer.className = 'pms-summary-items';

    if (count === 0) {
      itemsContainer.innerHTML = `
        <div class="pms-summary-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <p>${this.escapeHtml(this.config.emptyMessage || 'No items added yet')}</p>
        </div>
      `;
    } else {
      for (const item of items) {
        const itemEl = this.createItemElement(item);
        itemsContainer.appendChild(itemEl);
      }
    }

    this.container.appendChild(itemsContainer);

    // Footer with context summary, total, and smart actions
    if (count > 0) {
      const footer = document.createElement('div');
      footer.className = 'pms-summary-footer';

      // Context summary (e.g., "3 items in your build" or "Comparing 2 vehicles")
      footer.innerHTML += `
        <div class="pms-summary-context">
          <span class="pms-summary-context-text">${this.escapeHtml(context.summary)}</span>
        </div>
      `;

      // Total (for shopping/priced items)
      if (this.config.showTotal && total) {
        footer.innerHTML += `
          <div class="pms-summary-total">
            <span class="pms-summary-total-label">Total</span>
            <span class="pms-summary-total-value">${this.escapeHtml(total)}</span>
          </div>
        `;
      }

      // Smart contextual actions (from context detection)
      const smartActions = context.suggestedActions.length > 0
        ? context.suggestedActions
        : this.config.actions || [];

      if (smartActions.length > 0) {
        const actionsHtml = smartActions
          .slice(0, 3) // Limit to 3 actions
          .map((action: SummaryAction | ContextAction) => {
            const minItems = 'minItems' in action ? action.minItems : undefined;
            if (minItems && count < minItems) return '';
            const variant = action.variant === 'primary' ? 'pms-summary-action-primary' : '';
            return `<button class="pms-summary-action ${variant}" data-action="${this.escapeHtml(action.value)}">${this.escapeHtml(action.label)}</button>`;
          })
          .filter(Boolean)
          .join('');

        footer.innerHTML += `<div class="pms-summary-actions">${actionsHtml}</div>`;
      }

      // Quick search suggestion based on categories
      const categories = this.listStore.getCategories();
      const firstCategory = categories[0];
      if (firstCategory) {
        footer.innerHTML += `
          <div class="pms-summary-suggestion">
            <button class="pms-summary-suggestion-btn" data-action="search_more_${firstCategory}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              Find more ${this.escapeHtml(firstCategory)}
            </button>
          </div>
        `;
      }

      this.container.appendChild(footer);

      // Bind action buttons
      const actionBtns = footer.querySelectorAll('.pms-summary-action, .pms-summary-suggestion-btn');
      actionBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const action = (btn as HTMLElement).dataset['action'];
          if (action && this.config.onAction) {
            this.config.onAction(action, this.listStore.getItems());
          }
        });
      });
    }
  }

  /**
   * Create an item element.
   */
  private createItemElement(item: ListItem): HTMLElement {
    const el = document.createElement('div');
    el.className = 'pms-summary-item';
    el.dataset['itemId'] = item.id;

    const priceHtml = item.price
      ? `<span class="pms-summary-item-price">${new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: item.currency || 'GBP',
          minimumFractionDigits: 0,
        }).format(item.price)}</span>`
      : '';

    el.innerHTML = `
      <div class="pms-summary-item-content">
        ${item.image ? `<img class="pms-summary-item-image" src="${this.escapeHtml(item.image)}" alt="" />` : ''}
        <div class="pms-summary-item-details">
          <span class="pms-summary-item-title">${this.escapeHtml(item.title)}</span>
          ${item.subtitle ? `<span class="pms-summary-item-subtitle">${this.escapeHtml(item.subtitle)}</span>` : ''}
        </div>
      </div>
      <div class="pms-summary-item-actions">
        ${priceHtml}
        <button class="pms-summary-item-remove" title="Remove">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `;

    // Bind remove button
    const removeBtn = el.querySelector('.pms-summary-item-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.handleRemoveItem(item.id);
      });
    }

    return el;
  }

  /**
   * Handle removing an item.
   */
  private handleRemoveItem(itemId: string): void {
    // Add animation before removing
    const itemEl = this.container.querySelector(`[data-item-id="${itemId}"]`);
    if (itemEl) {
      itemEl.classList.add('pms-summary-item-removing');
      setTimeout(() => {
        this.listStore.removeItem(itemId);
      }, 200);
    } else {
      this.listStore.removeItem(itemId);
    }
  }

  /**
   * Handle clearing all items.
   */
  private handleClear(): void {
    if (confirm('Remove all items from your list?')) {
      this.listStore.clear();
    }
  }

  /**
   * Open the summary panel (for mobile).
   */
  open(): void {
    this.isOpen = true;
    this.container.classList.add('pms-open');
  }

  /**
   * Close the summary panel.
   */
  close(): void {
    this.isOpen = false;
    this.container.classList.remove('pms-open');
    if (this.config.onClose) {
      this.config.onClose();
    }
  }

  /**
   * Toggle the summary panel.
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Check if the panel is open.
   */
  isVisible(): boolean {
    return this.isOpen;
  }

  /**
   * Get current item count.
   */
  getItemCount(): number {
    return this.listStore.getCount();
  }

  /**
   * Cleanup.
   */
  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.renderResetTimeout) {
      clearTimeout(this.renderResetTimeout);
    }
    this.container.innerHTML = '';
  }

  /**
   * Escape HTML characters.
   */
  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
